import { Icon } from "@/components";
import { buildPathWithParams, useMediaQuery, useQueryString } from "@/hooks";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { colors } from "./colors";
import { PRIVACY_POLICY } from "./footer_v2";
import styles from "./discount_popup.module.css";
import classNames from "classnames";
import { isEmail } from "@/lib/validation/string";
import { BsCopy } from "react-icons/bs";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { useHistory } from "react-router-dom";
import { usePartnerDiscountPopupQuery } from "../hooks/use_partner_discount_popup_query";
import { hideDiscountPopupFBInLocalStorage } from "@/core/discount_popup_provider";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { useAnalytics } from "@/lib/analytics/analytics";

const DISCOUNT_POPUP_SESSION = "showDiscountPopup";

interface QueryParams {
  promoCode?: string;
}

interface DiscountPopupProps {
  partnerId: string;
  offset?: number;
}

export function DiscountPopup({ partnerId, offset }: DiscountPopupProps) {
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";
  const history = useHistory();
  const { promoCode: promoCodeParam } = useQueryString<QueryParams>();
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  const { data: discountPopupData } = usePartnerDiscountPopupQuery(partnerId);

  const isCodeApplied = promoCodeParam === discountPopupData?.promotion.code;
  const isCheckoutPage = window.location.pathname.includes("/checkout");
  const savingDisplay =
    discountPopupData?.promotion.offerType === "percentage"
      ? `${Number(discountPopupData.promotion.offerValue)}%`
      : undefined;
  const hideDiscountPopupSession =
    sessionStorage.getItem(DISCOUNT_POPUP_SESSION) === "false";
  const isDifferentCodeApplied = !!promoCodeParam && !isCodeApplied;

  const hideFloatingButtonInitialState =
    isDifferentCodeApplied ||
    (isCheckoutPage && !!promoCodeParam) ||
    !savingDisplay;

  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [showCodeCopiedToast, setShowCodeCopiedToast] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  const handleCloseFloatingBtn = () => {
    hideDiscountPopupFBInLocalStorage();
    setShowFloatingButton(false);
  };

  useEffect(() => {
    const displayModalTimeout = setTimeout(() => {
      if (!hideDiscountPopupSession && !promoCodeParam) {
        setShowModal(true);
      }
    }, 8000);

    if (!hideFloatingButtonInitialState) {
      setShowFloatingButton(true);
    }

    return () => clearTimeout(displayModalTimeout);
  }, [
    promoCodeParam,
    hideFloatingButtonInitialState,
    hideDiscountPopupSession,
  ]);

  if (!discountPopupData || !savingDisplay) {
    return null;
  }

  const handleClickSignUp = () => {
    if (!isEmail(email)) {
      setIsEmailValid(false);
    } else {
      setIsEmailValid(true);
      const updatedUrl = buildPathWithParams(window.location.pathname, {
        promoCode: discountPopupData.promotion.code,
      });
      history.replace(updatedUrl);

      analytics.identify("", { email });
      analytics.event(
        "Sign Up For Discount",
        {
          Email: email,
          "Promotion Code": discountPopupData.promotion.code,
          "Promotion Discount":
            discountPopupData.promotion.offerType === "percentage"
              ? `${Number(discountPopupData.promotion.offerValue).toFixed(0)}%`
              : `$${discountPopupData.promotion.offerValue}`,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    }
  };

  const handleClickCopyCode = () => {
    navigator.clipboard.writeText(discountPopupData.promotion.code);
    setShowCodeCopiedToast(true);
    setTimeout(() => {
      setShowCodeCopiedToast(false);
    }, 3000);
  };

  const handleCloseModal = () => {
    sessionStorage.setItem(DISCOUNT_POPUP_SESSION, "false");
    setShowModal(false);
  };

  return (
    <div>
      <Modal
        show={showModal}
        fullscreen={isMobile || undefined}
        dialogClassName={styles.dialog}
        contentClassName={styles.modalContent}
        centered
      >
        <div
          className={classNames(
            "d-flex",
            isMobile
              ? "flex-column position-fixed bottom-0 w-100"
              : "flex-column-reverse"
          )}
        >
          <div
            className={classNames(
              "bg-transparent d-flex justify-content-center",
              showCodeCopiedToast ? "opacity-100" : "opacity-0",
              isMobile ? "pb-4" : "pt-3"
            )}
          >
            <div
              className="p-2 text-white rounded"
              style={{
                background: colors.spinachCore,
                width: "fit-content",
              }}
            >
              {fbt("Promo code copied to clipboard!")}
            </div>
          </div>

          <div
            className="d-flex text-black bg-white "
            style={{
              height: isMobile ? "fit-content" : 660,
              maxHeight: isMobile ? "80vh" : "unset",
            }}
          >
            {!isMobile && (
              <div className={styles.imageWrapper}>
                <img
                  src={discountPopupData.imageUrl}
                  alt="discount popup"
                  className="w-100 h-100 object-fit-cover"
                />
              </div>
            )}
            <div
              className={classNames(
                "pb-5 overflow-y-auto",
                styles.contentWrapper,
                isMobile ? "p-3 w-100" : "p-4"
              )}
            >
              <div className="d-flex flex-row-reverse">
                <button
                  className="border-0 bg-transparent"
                  onClick={handleCloseModal}
                >
                  <Icon name="x" color="#000" />
                </button>
              </div>
              {isCodeApplied ? (
                <div>
                  <div
                    className="mt-3 fw-bold"
                    style={{ fontSize: isMobile ? 28 : 41 }}
                  >
                    {fbt("Thank you for signing up!")}
                  </div>
                  <div className="mt-3 fw-semibold">
                    {fbt(
                      "Your " +
                        fbt.param("savingDisplay", savingDisplay) +
                        " off code is"
                    )}
                    :
                  </div>
                  <div className="d-flex justify-content-between mt-3 fw-bold">
                    <div
                      className="text-decoration-underline"
                      style={{ fontSize: 28 }}
                      role="button"
                      onClick={handleClickCopyCode}
                    >
                      {discountPopupData.promotion.code}
                    </div>
                    <button
                      className="border-0 bg-transparent"
                      onClick={handleClickCopyCode}
                    >
                      <BsCopy size={28} />
                    </button>
                  </div>
                  <div
                    style={{
                      backgroundColor: colors.spinachMinus80,
                      color: colors.spinachCore,
                    }}
                    className="px-2 py-3 rounded d-flex align-items-center gap-2 mt-4 fw-semibold"
                  >
                    <div>
                      <IoIosCheckmarkCircleOutline size={16} />
                    </div>
                    <div>
                      {fbt(
                        "Your promotion code will be applied automatically at checkout."
                      )}
                    </div>
                  </div>
                  <Button
                    className="w-100 mt-3 py-3 fw-semibold"
                    style={{ height: 60 }}
                    onClick={() => setShowModal(false)}
                  >
                    {fbt("Continue")}
                  </Button>
                </div>
              ) : (
                <div>
                  <div
                    className="mt-3 fw-bold"
                    style={{ fontSize: isMobile ? 28 : 41 }}
                  >
                    {fbt(
                      "Get " +
                        fbt.param("savingDisplay", savingDisplay) +
                        " off your next order!"
                    )}
                  </div>
                  <div className="mt-3 fw-semibold">
                    {fbt(
                      "Sign up to receive exclusive promotions and discounts, and receive " +
                        fbt.param("savingDisplay", savingDisplay) +
                        " off your next order!"
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="fw-semibold">{fbt("Email")}</div>
                    <Form.Control
                      type="email"
                      className="mt-1 popup-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!isEmailValid}
                    />
                    {!isEmailValid && (
                      <div className="text-danger mt-1">
                        {fbt("Please provide a valid email.")}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-1 mt-3">
                    <Button
                      className="fw-semibold"
                      style={{ height: 60 }}
                      onClick={handleClickSignUp}
                    >
                      {fbt("Sign up")}
                    </Button>
                    <button
                      className="border-0 bg-transparent text-primary p-2"
                      onClick={handleCloseModal}
                    >
                      {fbt("Not now")}
                    </button>
                  </div>
                  <div className="mt-3" style={{ color: colors.blackMinus60 }}>
                    {fbt(
                      "By signing up, you agree to receive emails related to your discount. One discount per customer/email. Not combinable with other offers."
                    )}{" "}
                    See our{" "}
                    <a href={PRIVACY_POLICY} target="_blank" rel="noreferrer">
                      Privacy Policy
                    </a>
                    .
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {showFloatingButton && (
        <div
          className={classNames(
            "bg-black opacity-75 p-3 rounded text-white border-0",
            isMobile ? "m-1" : "m-4"
          )}
          style={{
            width: "fit-content",
            position: "fixed",
            left: 0,
            bottom: offset || 0,
          }}
        >
          <div className="d-flex align-items-center gap-1 fw-semibold lh-1">
            <div role="button" onClick={() => setShowModal(true)}>
              {isCodeApplied
                ? fbt(
                    fbt.param("savingDisplay", savingDisplay) +
                      " off applied at checkout"
                  )
                : fbt(
                    "Get " + fbt.param("savingDisplay", savingDisplay) + " Off!"
                  )}
            </div>
            <button
              className="border-0 bg-transparent"
              onClick={handleCloseFloatingBtn}
            >
              <Icon name="x" color="#fff" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
